import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Box,
    Card,
    CardContent,
    Divider,
    Chip,
    Tabs,
    Tab,
    LinearProgress,
    CircularProgress
} from '@mui/material';
import {
    FaCheckCircle,
    FaClock,
    FaStar,
    FaHistory,
    FaQuestionCircle,
    FaChartLine,
    FaTimesCircle
} from 'react-icons/fa';
import DataTable from '../DataTable';
import teacherService from '../../services/teacherService';

const StatBox = ({ label, value, icon, color }) => (
    <Card sx={{ height: '100%', bgcolor: '#fcfcfc' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ mr: 2, color: color, fontSize: 24 }}>{icon}</Box>
            <Box>
                <Typography variant="caption" color="textSecondary" display="block">{label}</Typography>
                <Typography variant="h6" fontWeight="bold">{value}</Typography>
            </Box>
        </CardContent>
    </Card>
);

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
    return (
        <div hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const StudentProfileModal = ({ open, onClose, studentId, fetchProgressFn }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    // New analytics data
    const [questionHistory, setQuestionHistory] = useState(null);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    // Fetch lesson progress (existing)
    useEffect(() => {
        if (open && studentId && fetchProgressFn) {
            setLoading(true);
            fetchProgressFn(studentId)
                .then(setData)
                .catch(err => {
                    console.error("Failed to load progress", err);
                    setData(null);
                })
                .finally(() => setLoading(false));
        } else {
            setData(null);
        }
    }, [open, studentId, fetchProgressFn]);

    // Fetch question history when tab changes
    useEffect(() => {
        if (open && studentId && activeTab === 1 && !questionHistory) {
            setLoadingQuestions(true);
            teacherService.getStudentQuestionHistory(studentId, { days: 30 })
                .then(setQuestionHistory)
                .catch(err => {
                    console.error("Failed to load question history", err);
                    setQuestionHistory({ answers: [] });
                })
                .finally(() => setLoadingQuestions(false));
        }
    }, [open, studentId, activeTab, questionHistory]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setActiveTab(0);
            setQuestionHistory(null);
        }
    }, [open]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Lesson history columns
    const historyColumns = [
        { id: 'lesson_title', label: 'Lesson', render: (row) => row.lesson_title || row.lesson_id },
        {
            id: 'status', label: 'Status', render: (row) => (
                <Chip
                    label={row.status}
                    color={row.status === 'completed' ? 'success' : 'warning'}
                    size="small"
                    variant="outlined"
                />
            )
        },
        { id: 'score', label: 'Score', render: (row) => row.status === 'completed' ? `${row.score_percentage}%` : '-' },
        { id: 'time', label: 'Time', render: (row) => row.time_spent_seconds ? `${Math.round(row.time_spent_seconds / 60)} min` : '-' },
        { id: 'date', label: 'Date', render: (row) => new Date(row.last_updated_at || row.started_at).toLocaleDateString() }
    ];

    // Question history columns
    const questionColumns = [
        {
            id: 'question',
            label: 'Question',
            render: (row) => (
                <Box>
                    <Typography variant="body2" fontWeight="500">
                        {row.question_title || `Question ${row.question_number}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {row.lesson_title} • {row.question_type}
                    </Typography>
                </Box>
            )
        },
        {
            id: 'result',
            label: 'Result',
            render: (row) => (
                <Chip
                    icon={row.is_correct ? <FaCheckCircle size={12} /> : <FaTimesCircle size={12} />}
                    label={row.is_correct ? 'Correct' : 'Incorrect'}
                    color={row.is_correct ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                />
            )
        },
        { id: 'time', label: 'Time', render: (row) => row.time_spent_seconds ? `${row.time_spent_seconds}s` : '-' },
        { id: 'attempt', label: 'Attempt', render: (row) => row.attempt_number || 1 },
        { id: 'date', label: 'Date', render: (row) => new Date(row.created_at).toLocaleString() }
    ];

    // Calculate question stats
    const getQuestionStats = () => {
        if (!questionHistory?.answers?.length) return { total: 0, correct: 0, accuracy: 0 };
        const total = questionHistory.answers.length;
        const correct = questionHistory.answers.filter(a => a.is_correct).length;
        return {
            total,
            correct,
            accuracy: total > 0 ? Math.round((correct / total) * 100) : 0
        };
    };

    if (!open) return null;

    const qStats = getQuestionStats();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <FaChartLine style={{ marginRight: 10 }} />
                    Student Analytics & Progress
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={5}>
                        <CircularProgress />
                    </Box>
                ) : data ? (
                    <>
                        {/* Stats Row */}
                        <Grid container spacing={2} mb={3}>
                            <Grid item xs={12} sm={3}>
                                <StatBox
                                    label="Lessons Completed"
                                    value={`${data.stats.total_lessons_completed} / ${data.stats.total_lessons_started}`}
                                    icon={<FaCheckCircle />}
                                    color="#2e7d32"
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <StatBox
                                    label="Avg. Score"
                                    value={`${data.stats.average_score}%`}
                                    icon={<FaStar />}
                                    color="#ed6c02"
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <StatBox
                                    label="Time Spent"
                                    value={`${data.stats.total_time_spent_minutes} min`}
                                    icon={<FaClock />}
                                    color="#1976d2"
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <StatBox
                                    label="Questions Answered"
                                    value={qStats.total > 0 ? `${qStats.correct}/${qStats.total}` : '-'}
                                    icon={<FaQuestionCircle />}
                                    color="#9c27b0"
                                />
                            </Grid>
                        </Grid>

                        {/* Accuracy Progress Bar */}
                        {qStats.total > 0 && (
                            <Box mb={3}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2" fontWeight="500">
                                        Question Accuracy
                                    </Typography>
                                    <Typography variant="body2" color="primary" fontWeight="bold">
                                        {qStats.accuracy}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={qStats.accuracy}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {/* Tabs */}
                        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 1 }}>
                            <Tab icon={<FaHistory size={14} />} iconPosition="start" label="Lesson History" />
                            <Tab icon={<FaQuestionCircle size={14} />} iconPosition="start" label="Question Details" />
                        </Tabs>

                        {/* Lesson History Tab */}
                        <TabPanel value={activeTab} index={0}>
                            <DataTable
                                columns={historyColumns}
                                data={data.history}
                                loading={false}
                                count={data.history.length}
                                page={1}
                                rowsPerPage={100}
                            />
                        </TabPanel>

                        {/* Question Details Tab */}
                        <TabPanel value={activeTab} index={1}>
                            {loadingQuestions ? (
                                <Box display="flex" justifyContent="center" py={3}>
                                    <CircularProgress size={30} />
                                </Box>
                            ) : questionHistory?.answers?.length > 0 ? (
                                <DataTable
                                    columns={questionColumns}
                                    data={questionHistory.answers}
                                    loading={false}
                                    count={questionHistory.answers.length}
                                    page={1}
                                    rowsPerPage={50}
                                />
                            ) : (
                                <Typography align="center" color="textSecondary" py={3}>
                                    No question activity recorded yet.
                                </Typography>
                            )}
                        </TabPanel>
                    </>
                ) : (
                    <Typography align="center" color="error">Could not load student data.</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default StudentProfileModal;
